import { type IssueChartDataInfo } from 'pages/Desk/issues/chart_data/types';
import {
    createZustandContext,
    getZustandStoreShallowHook,
    ZustandContextInitializer,
} from 'shared/zustand_context/zustand_context';
import { firstItem } from 'utils/array';
import { findIndicatorGroup, groupCardDetail } from './card_detail/groupCardDetail';
import {
    type NeedsAttentionCard,
    type QueueAlert,
    type QueueHierarchyChild,
    type QueueIssue,
} from './data/queueTypes';
import {
    type DismissableSection,
    getRowDismissalSection,
    type RowDismissalKey,
} from './dismissal/dismissTargets';
import { type QueueRow, type QueueRowKey } from './queue/queueRowKey';

export type InvestigationTarget =
    | { type: 'alert'; alertId: string }
    | { type: 'issue'; issueId: string };

/** An Indicator's Alerts, grouped as listed in the queue. */
export type InvestigatedAlertGroup = Readonly<{
    type: 'alert';
    /**
     * the Issue the group is listed under, or `null` when unlinked (or staged
     * into a pending-create Issue)
     */
    issueId: string | null;
    mpId: string;
    indicatorId: string;
    /** never empty */
    alertIds: readonly string[];
}>;

export type InvestigatedIssue = Readonly<{ type: 'issue'; issueId: string }>;

/** An event that can be investigated, and acted on from the investigation ribbon. */
export type InvestigatedItem = InvestigatedAlertGroup | InvestigatedIssue;

/**
 * Whether `a` and `b` are the same investigable item. Alert groups are the
 * same when listed in the same place, even if their Alerts have since changed.
 */
// (this distinction is necessary because you can have alerts for the same
// indicator under an different issues, or under different issues and unlinked.
// This _is_ a meaningful distinction, because different individual alerts may
// have happened at different times. Considering that, of course, reveals that
// we can only meaningfully choose which alert we want to investigate if they're
// not grouped together... And there is probably a better UX achievable by a less
// tired JP. Thankfully, this only affects the selected time range.)
export function isSameInvestigatedItem(
    a: InvestigatedItem | undefined,
    b: InvestigatedItem,
): boolean {
    if (a?.type !== b.type) {
        return false;
    }
    switch (b.type) {
        case 'alert':
            return (
                a.type === 'alert' &&
                a.issueId === b.issueId &&
                a.mpId === b.mpId &&
                a.indicatorId === b.indicatorId
            );
        case 'issue':
            return a.type === 'issue' && a.issueId === b.issueId;
    }
}

/** Tabs of the workspace pane, which double as their route segments. */
export type WorkspacePaneId = 'event' | 'investigation';

/** The event currently shown in the Event Details tab. */
export type FocusedEvent = { type: 'issue'; issueId: string };

/** The issue-centric queue sections, whose rows are each an Issue. */
export type IssueQueueSection = 'followUp' | 'closeout';

/**
 * The queue row driving the workspace pane, by id. Its data is read from
 * the queue, so that both render the same data.
 */
export type MissionControlSelection =
    | Readonly<{ type: 'assembly'; assemblyId: string }>
    | Readonly<{ type: 'issue'; section: IssueQueueSection; issueId: string }>;

// ---
// Alert patch save flow
// ---

// Steps hold their Alerts as fetched, without staged link patches applied: a
// patched Alert's `Issue` may be a stand-in for a pending-create Issue, which
// the forms working on the step must not see.

/** Adjusting the thresholds of Alerts' Indicators, in `AdjustThresholdsModal`. */
export type AdjustThresholdsStep = Readonly<{
    kind: 'adjust-thresholds';
    id: string;
    locationId: string;
    alerts: QueueAlert[];
    /** the queue row the Alerts are listed in */
    row: QueueRow;
}>;

/**
 * Creating a new Issue from Alerts, e.g. ones staged into a pending-create
 * Issue.
 */
export type CreateIssueStep = Readonly<{
    kind: 'create-issue';
    /** e.g. the pending-create Issue id the Alerts were staged into */
    id: string;
    /**
     * the group the Issue is created on: the Alerts' closest common ancestor,
     * but no deeper than an Asset
     */
    group: QueueHierarchyChild;
    alerts: QueueAlert[];
    /**
     * Existing links to remove before the Issue is created, for Alerts that
     * were moved out of an existing Issue. Each holds the Issue as the card
     * listed it before the save.
     */
    sourceIssueUnlinks: ReadonlyArray<
        Readonly<{ issue: QueueIssue; alertIds: string[] }>
    >;
}>;

/** A step of an Alert patch save that needs user interaction to complete. */
export type InteractionStep = AdjustThresholdsStep | CreateIssueStep;

/**
 * A step goes from `not-started` to `working` (the one on screen) to `done` or
 * `skipped`. At most one step is `working` at a time; none is while the save
 * flow waits on its background save after the last step.
 */
export type InteractionStepStatus = 'not-started' | 'working' | 'done' | 'skipped';

export type BackgroundSaveStatus = 'saving' | 'saved' | 'failed';

/**
 * An Alert patch save in progress that needs user interaction. Its steps are
 * worked through one at a time, in order.
 */
export type AlertPatchSaveFlow = Readonly<{
    /** `undefined` when the save has no non-interactive part */
    backgroundSave: BackgroundSaveStatus | undefined;
    steps: ReadonlyArray<
        Readonly<{ step: InteractionStep; status: InteractionStepStatus }>
    >;
}>;

/** The step on screen, if any. */
export function getWorkingStep(state: MissionControlState): InteractionStep | undefined {
    return state.alertPatchSaveFlow?.steps.find((s) => s.status === 'working')?.step;
}

/** The working step, if it is a `create-issue` step. */
export function getCreateIssueStep(
    state: MissionControlState,
): CreateIssueStep | undefined {
    const step = getWorkingStep(state);
    return step?.kind === 'create-issue' ? step : undefined;
}

// ---
// Adding plots
// ---

/** The Issue whose plots are being added to, from the investigation Workbench. */
export type AddPlotsTarget = Readonly<{
    issueId: string;
    /** the Issue's number, as shown to the user */
    formattedIssueNumber: string;
    /** the plots, not yet saved, that adding plots starts with */
    initialChartData: readonly IssueChartDataInfo[];
}>;

/**
 * Whether the queue is blocked, in the middle of an Alert patch save flow or
 * adding plots, so that its selection can't change.
 */
export function isQueueBlocked(state: MissionControlState): boolean {
    return state.alertPatchSaveFlow !== undefined || state.addingPlotsTo !== undefined;
}

// ---
// Local dismissals
// ---

/**
 * A queue row dismissed this session. Dismissed rows stay listed (greyed out)
 * until their section next refetches.
 */
export type LocalDismissal = Readonly<{
    /** `undefined` for an indefinite dismissal */
    remindAfter: Date | undefined;
    /** epoch ms, comparable to a query's `dataUpdatedAt` */
    dismissedAt: number;
}>;

// ---
// Row errors
// ---

/** What a queue row's error is about. A row has at most one error per source. */
export type RowErrorSource = 'acknowledge' | 'save-alerts' | 'dismiss';

export type RowError = Readonly<{ source: RowErrorSource; message: string }>;

// ---
// Store
// ---

export type MissionControlState = {
    selection: MissionControlSelection | undefined;
    /**
     * The workspace tab currently on screen, or `undefined` when the
     * workspace pane isn't showing a tab.
     */
    activePane: WorkspacePaneId | undefined;
    /**
     * The event under investigation, which the investigation ribbon shows and
     * acts on. Persists until replaced, cleared, or the selection changes.
     */
    investigating: InvestigatedItem | undefined;
    /**
     * Transient command to point the investigation Workbench at an event.
     * Cleared once applied, so the user is free to diverge from it afterwards.
     */
    investigationTarget: InvestigationTarget | undefined;
    /**
     * The event shown in Event Details. Persists until replaced or the
     * selection changes; does not affect the Workbench.
     */
    focusedEvent: FocusedEvent | undefined;
    /**
     * Transient command to bring a workspace tab on screen. Cleared once
     * applied (or once that tab is already showing).
     */
    requestedPane: WorkspacePaneId | undefined;
    /**
     * The Alert patch save flow in progress, if any. While set, the selection
     * can't change.
     */
    alertPatchSaveFlow: AlertPatchSaveFlow | undefined;
    /**
     * The Issue being added plots to, if any. While set, the selection and the
     * workspace tab can't change.
     */
    addingPlotsTo: AddPlotsTarget | undefined;
    /**
     * The bar shown in place of the workspace tabs while the Workbench is
     * in a mode (e.g. creating an Issue), for the Workbench to portal the
     * mode's title and actions into. `null` while not mounted.
     */
    workbenchModeBar: HTMLElement | null;
    /** Rows dismissed since their section last fetched */
    localDismissals: ReadonlyMap<RowDismissalKey, LocalDismissal>;
    /** Errors shown under queue rows, until closed or their action next succeeds */
    rowErrors: ReadonlyMap<QueueRowKey, readonly RowError[]>;
    /**
     * Functions as a toggle. Selecting brings the Investigation tab on screen
     * and investigates the card's foremost trigger. Ignored while
     * `isQueueBlocked`.
     */
    selectCard: (card: NeedsAttentionCard) => void;
    /**
     * Selects and investigates the issue listed in `section`, and brings it on
     * screen in Event Details. Not a toggle -- reselecting the same issue
     * re-shows it. Ignored while `isQueueBlocked`.
     */
    selectIssue: (section: IssueQueueSection, issueId: string) => void;
    /** Ignored while `isQueueBlocked`. */
    clearSelection: () => void;
    setActivePane: (pane: WorkspacePaneId | undefined) => void;
    /** pass as the `ref` of the bar element that `workbenchModeBar` holds */
    setWorkbenchModeBar: (bar: HTMLElement | null) => void;
    /**
     * Investigates the group, pointing the investigation Workbench at its
     * first alert and bringing the Investigation tab on screen. Also focuses
     * the group's issue (if any) in Event Details.
     */
    investigateAlertGroup: (group: InvestigatedAlertGroup) => void;
    /**
     * Investigates the issue and focuses it in Event Details, and points the
     * Workbench at it when the Investigation tab is on screen. Does not change
     * the active tab.
     */
    investigateIssue: (issueId: string) => void;
    /** Stops investigating, leaving Event Details and the Workbench as they are. */
    clearInvestigating: () => void;
    /**
     * Focuses the issue in Event Details without touching the Workbench. With
     * `show`, also brings the Event Details tab on screen.
     */
    focusIssue: (issueId: string, options?: { show?: boolean }) => void;
    /** Brings the workspace tab `pane` on screen. Ignored while `addingPlotsTo`. */
    showPane: (pane: WorkspacePaneId) => void;
    clearInvestigationTarget: () => void;
    clearRequestedPane: () => void;
    /**
     * Starts an `alertPatchSaveFlow`, with the first of `steps` as the working
     * step. Not while `isQueueBlocked`.
     *
     * @param backgroundSave the status of the save's non-interactive part, or
     * `undefined` if it has none
     */
    startAlertPatchSaveFlow: (
        steps: InteractionStep[],
        backgroundSave: BackgroundSaveStatus | undefined,
    ) => void;
    setBackgroundSaveStatus: (status: BackgroundSaveStatus) => void;
    /**
     * Marks the working step `outcome`, and makes the next step the working
     * one. The save flow ends once every step is resolved and the background
     * save isn't running.
     */
    finishInteractionStep: (stepId: string, outcome: 'done' | 'skipped') => void;
    /**
     * Starts adding plots to `issue`, from the Workbench as it is. Not while
     * `isQueueBlocked`.
     */
    startAddingPlots: (issue: AddPlotsTarget) => void;
    /** Ends `addingPlotsTo`, e.g. once the plots are saved or discarded. */
    stopAddingPlots: () => void;
    recordLocalDismissal: (key: RowDismissalKey, dismissal: LocalDismissal) => void;
    forgetLocalDismissal: (key: RowDismissalKey) => void;
    /**
     * Call when `section`'s active listing refetches, since its data then
     * reflects the dismissals made before it
     *
     * @param fetchedAt the fetch's `dataUpdatedAt`
     */
    forgetLocalDismissals: (section: DismissableSection, fetchedAt: number) => void;
    /** Replaces the row's error from the same source, if any */
    reportRowError: (key: QueueRowKey, error: RowError) => void;
    clearRowError: (key: QueueRowKey, source: RowErrorSource) => void;
    /**
     * Pass a row's element once it mounts, and `null` once it unmounts. Not
     * reactive -- read it with `getRowElement`.
     */
    setRowElement: (key: QueueRowKey, element: HTMLElement | null) => void;
    getRowElement: (key: QueueRowKey) => HTMLElement | undefined;
};

export const MissionControlContext =
    createZustandContext<MissionControlState>('MissionControlStore');

export const missionControlStoreInitializer: ZustandContextInitializer<
    MissionControlState
> = (set, get) => {
    // kept out of the state, since elements change nothing rendered
    const rowElements = new Map<QueueRowKey, HTMLElement>();
    return {
        selection: undefined,
        activePane: undefined,
        investigating: undefined,
        investigationTarget: undefined,
        focusedEvent: undefined,
        requestedPane: undefined,
        alertPatchSaveFlow: undefined,
        addingPlotsTo: undefined,
        workbenchModeBar: null,
        localDismissals: new Map(),
        rowErrors: new Map(),
        selectCard: (card) => {
            if (isQueueBlocked(get())) {
                return;
            }
            const current = get().selection;
            const isSame =
                current?.type === 'assembly' && current.assemblyId === card.AssemblyId;
            const cleared = {
                investigating: undefined,
                investigationTarget: undefined,
                focusedEvent: undefined,
            };
            if (isSame) {
                set({ selection: undefined, requestedPane: undefined, ...cleared });
                return;
            }

            const trigger = getForemostTrigger(card);
            set({
                selection: { type: 'assembly', assemblyId: card.AssemblyId },
                ...cleared,
                requestedPane: 'investigation',
                ...(trigger &&
                    getInvestigateState(trigger, { isInvestigationOnScreen: true })),
            });
        },
        selectIssue: (section, issueId) => {
            if (isQueueBlocked(get())) {
                return;
            }
            set({
                selection: { type: 'issue', section, issueId },
                investigating: { type: 'issue', issueId },
                investigationTarget: undefined,
            });
            get().focusIssue(issueId, { show: true });
        },
        clearSelection: () => {
            if (isQueueBlocked(get()) || !get().selection) {
                return;
            }
            set({
                selection: undefined,
                investigating: undefined,
                investigationTarget: undefined,
                focusedEvent: undefined,
                requestedPane: undefined,
            });
        },
        setWorkbenchModeBar: (bar) => {
            set({ workbenchModeBar: bar });
        },
        setActivePane: (pane) => {
            set({ activePane: pane });
        },
        investigateAlertGroup: (group) => {
            set(getInvestigateState(group, { isInvestigationOnScreen: true }));
        },
        investigateIssue: (issueId) => {
            set(
                getInvestigateState(
                    { type: 'issue', issueId },
                    { isInvestigationOnScreen: get().activePane === 'investigation' },
                ),
            );
        },
        clearInvestigating: () => {
            set({ investigating: undefined });
        },
        focusIssue: (issueId, options) => {
            set({
                focusedEvent: { type: 'issue', issueId },
                ...(options?.show && { requestedPane: 'event' }),
            });
        },
        showPane: (pane) => {
            if (get().addingPlotsTo) {
                return;
            }
            set({ requestedPane: pane });
        },
        clearInvestigationTarget: () => {
            set({ investigationTarget: undefined });
        },
        clearRequestedPane: () => {
            set({ requestedPane: undefined });
        },
        startAlertPatchSaveFlow: (steps, backgroundSave) => {
            if (get().alertPatchSaveFlow) {
                throw new Error('An Alert patch save flow is already in progress');
            }
            if (get().addingPlotsTo) {
                throw new Error("Can't save Alert changes while adding plots");
            }
            const first = firstItem(steps);
            if (!first) {
                throw new Error('An Alert patch save flow needs at least one step');
            }
            set({
                alertPatchSaveFlow: {
                    backgroundSave,
                    steps: steps.map((step) => ({
                        step,
                        status: step === first ? 'working' : 'not-started',
                    })),
                },
                ...getStepStartState(first),
            });
        },
        setBackgroundSaveStatus: (status) => {
            const saveFlow = get().alertPatchSaveFlow;
            if (!saveFlow?.backgroundSave) {
                throw new Error(
                    'No Alert patch save flow with a background save is in progress',
                );
            }
            set({
                alertPatchSaveFlow: endSaveFlowIfComplete({
                    ...saveFlow,
                    backgroundSave: status,
                }),
            });
        },
        finishInteractionStep: (stepId, outcome) => {
            const saveFlow = get().alertPatchSaveFlow;
            const workingIndex =
                saveFlow?.steps.findIndex((s) => s.status === 'working') ?? -1;
            if (!saveFlow || saveFlow.steps.at(workingIndex)?.step.id !== stepId) {
                throw new Error(`Step "${stepId}" is not the working step`);
            }

            const next = saveFlow.steps.at(workingIndex + 1);
            const steps = saveFlow.steps.map((s, i) => {
                if (i === workingIndex) {
                    return { ...s, status: outcome };
                }
                if (i === workingIndex + 1) {
                    return { ...s, status: 'working' as const };
                }
                return s;
            });
            set({
                alertPatchSaveFlow: endSaveFlowIfComplete({ ...saveFlow, steps }),
                ...(next && getStepStartState(next.step)),
            });
        },
        startAddingPlots: (issue) => {
            if (isQueueBlocked(get())) {
                throw new Error("Can't add plots while the queue is blocked");
            }
            set({ addingPlotsTo: issue });
        },
        stopAddingPlots: () => {
            set({ addingPlotsTo: undefined });
        },
        recordLocalDismissal: (key, dismissal) => {
            set({ localDismissals: new Map(get().localDismissals).set(key, dismissal) });
        },
        forgetLocalDismissal: (key) => {
            const localDismissals = new Map(get().localDismissals);
            if (localDismissals.delete(key)) {
                set({ localDismissals });
            }
        },
        forgetLocalDismissals: (section, fetchedAt) => {
            const current = get().localDismissals;
            const kept = [...current].filter(
                ([key, dismissal]) =>
                    getRowDismissalSection(key) !== section ||
                    dismissal.dismissedAt >= fetchedAt,
            );
            if (kept.length !== current.size) {
                set({ localDismissals: new Map(kept) });
            }
        },
        reportRowError: (key, error) => {
            const others = (get().rowErrors.get(key) ?? []).filter(
                (e) => e.source !== error.source,
            );
            set({ rowErrors: new Map(get().rowErrors).set(key, [...others, error]) });
        },
        clearRowError: (key, source) => {
            const current = get().rowErrors.get(key);
            if (!current?.some((e) => e.source === source)) {
                return;
            }
            const rowErrors = new Map(get().rowErrors);
            const kept = current.filter((e) => e.source !== source);
            if (kept.length > 0) {
                rowErrors.set(key, kept);
            } else {
                rowErrors.delete(key);
            }
            set({ rowErrors });
        },
        setRowElement: (key, element) => {
            if (element) {
                rowElements.set(key, element);
            } else {
                rowElements.delete(key);
            }
        },
        getRowElement: (key) => rowElements.get(key),
    };
};

/**
 * State changes that investigate `item`. Alerts are always shown in the
 * Investigation tab; issues are only pointed the Workbench at when
 * `isInvestigationOnScreen`.
 */
function getInvestigateState(
    item: InvestigatedItem,
    { isInvestigationOnScreen }: { isInvestigationOnScreen: boolean },
): Partial<MissionControlState> {
    switch (item.type) {
        case 'alert': {
            const alertId = item.alertIds.at(0);
            if (!alertId) {
                throw new Error(`Alert group for indicator ${item.indicatorId} is empty`);
            }
            return {
                investigating: item,
                investigationTarget: { type: 'alert', alertId },
                requestedPane: 'investigation',
                ...(item.issueId !== null && {
                    focusedEvent: { type: 'issue', issueId: item.issueId },
                }),
            };
        }
        case 'issue':
            return {
                investigating: item,
                focusedEvent: { type: 'issue', issueId: item.issueId },
                ...(isInvestigationOnScreen && {
                    investigationTarget: { type: 'issue', issueId: item.issueId },
                }),
            };
    }
}

/**
 * The card's most important trigger, from its inclusion reasons, or
 * `undefined` when none of them name an event.
 */
function getForemostTrigger(card: NeedsAttentionCard): InvestigatedItem | undefined {
    const reason = card.InclusionReasons.find((r) => r.IssueId || r.AlertId);
    if (reason?.IssueId) {
        return { type: 'issue', issueId: reason.IssueId };
    }
    if (!reason?.AlertId) {
        return undefined;
    }
    const located = findIndicatorGroup(groupCardDetail(card), reason.AlertId);
    if (!located) {
        throw new Error(
            `Inclusion reason alert ${reason.AlertId} is not on card ${card.AssemblyId}`,
        );
    }
    return {
        type: 'alert',
        issueId: located.issueId,
        mpId: located.mpId,
        indicatorId: located.group.indicatorId,
        alertIds: located.group.alertIds,
    };
}

/** State changes that bring a step on screen as it becomes the working step. */
function getStepStartState(step: InteractionStep): Partial<MissionControlState> {
    switch (step.kind) {
        case 'adjust-thresholds':
            // shown in a modal
            return {};
        case 'create-issue': {
            const firstAlert = firstItem(step.alerts);
            return {
                requestedPane: 'investigation',
                ...(firstAlert && {
                    investigationTarget: { type: 'alert', alertId: firstAlert.Id },
                }),
            };
        }
    }
}

function endSaveFlowIfComplete(
    saveFlow: AlertPatchSaveFlow,
): AlertPatchSaveFlow | undefined {
    const isComplete =
        saveFlow.backgroundSave !== 'saving' &&
        saveFlow.steps.every((s) => s.status === 'done' || s.status === 'skipped');
    return isComplete ? undefined : saveFlow;
}

export const useMissionControlStore = getZustandStoreShallowHook(MissionControlContext);
